import { CloseOutlined, ZoomIn, ZoomOut } from '@mui/icons-material';
import { Box, IconButton, Modal, Typography, Dialog, DialogContent, Button, DialogActions } from '@mui/material'
import { useModal } from 'Modals'
import React, { useState, useRef } from 'react'

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'auto',
    height: '80vh',
    bgcolor: 'secondary.light',
    border: '1px solid #000',
    borderRadius: 2,
    boxShadow: 24,
}

const buttonStyle = {
    position: 'absolute',
    top: '-8%',
    right: '-8%',
    bgcolor: '#9e9e9e50',
    color: 'white'
}

const ImageModal = () => {
    const { imageModal: { show, title, link, zoomable }, hideImage } = useModal()

    if (zoomable) {
        return <ZoomableImageModal open={show} onClose={hideImage} imageUrl={link} />
    }

    return (
        <Modal
            open={show}
            onClose={hideImage}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            style={{ zIndex: 9999 }}
        >
            <div>
                <Box sx={style}>
                    <IconButton color='inherit' sx={buttonStyle} onClick={hideImage}>
                        <CloseOutlined />
                    </IconButton>
                    {title ? (
                        <Typography id="modal-modal-title" variant="h6" component="h2">
                            {title}
                        </Typography>
                    ) : null}
                    <div style={{ overflow: 'hidden', borderRadius: 8, height: '100%', width: '100%' }}>
                        {link ? (
                            <img
                                srcSet={`${link}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                                src={`${link}?w=164&h=164&fit=crop&auto=format`}
                                alt={link}
                                height="auto"
                                style={{ width: '80vw', backgroundSize: 'crop', backgroundRepeat: 'no-repeat' }}
                                loading="lazy"
                            />
                        ) : null}
                    </div>
                </Box>
            </div>
        </Modal>
    )
}

export default function ZoomableImageModal({ open, onClose, imageUrl }) {
    const [scale, setScale] = useState(1)
    const [position, setPosition] = useState({ x: 0, y: 0 })
    const isDragging = useRef(false)
    const lastPosition = useRef({ x: 0, y: 0 })

    const handleDoubleClick = (e) => {
        if (scale === 1) {
            setScale(2)
            setPosition({ x: -e.nativeEvent.offsetX, y: -e.nativeEvent.offsetY })
        } else {
            resetView()
        }
    }

    const handleWheel = (e) => {
        e.preventDefault()
        const zoomIntensity = 0.1
        const newScale = Math.min(Math.max(scale - e.deltaY * zoomIntensity * 0.01, 1), 5)
        setScale(newScale)
    }

    const handleMouseDown = (e) => {
        isDragging.current = true
        lastPosition.current = { x: e.clientX, y: e.clientY }
    }

    const handleMouseUp = () => {
        isDragging.current = false
    }

    const handleMouseMove = (e) => {
        if (!isDragging.current) return
        const dx = e.clientX - lastPosition.current.x
        const dy = e.clientY - lastPosition.current.y
        setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }))
        lastPosition.current = { x: e.clientX, y: e.clientY }
    }

    const resetView = () => {
        setScale(1)
        setPosition({ x: 0, y: 0 })
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" style={{ zIndex: 9999 }}>
            <DialogContent
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseUp}
                onDoubleClick={handleDoubleClick}
                style={{
                    padding: 0,
                    overflow: 'hidden',
                    cursor: isDragging.current ? 'grabbing' : 'grab',
                    height: '80vh',
                }}
            >
                <img
                    src={imageUrl}
                    alt="Zoomable"
                    style={{
                        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                        transformOrigin: '0 0',
                        transition: isDragging.current ? 'none' : 'transform 0.1s ease-out',
                        userSelect: 'none',
                        pointerEvents: 'none',
                    }}
                    draggable={false}
                />
            </DialogContent>
            <DialogActions>
                <IconButton onClick={() => setScale((prev) => Math.min(prev + 0.25, 5))} size="small">
                    <ZoomIn />
                </IconButton>
                <IconButton onClick={resetView} size="small">
                    <ZoomOut />
                </IconButton>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}

export { ImageModal }